import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as fileSaver from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import * as XLSX from 'xlsx';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { MessageComponent } from 'src/app/popups/message/message.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

@Injectable({
  providedIn: 'root'
})
export class FileSaveService {

  constructor(private spinner: NgxSpinnerService, private http: HttpClient, private modalService: NgbModal) { }

  DownloadFile = (url: string, filename: string, failureMessage?: string): void => {
    this.spinner.show();
    this.http.get(url, { responseType: 'blob' }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    ).subscribe({
      next: (blob) => {
        const fileBlob = new Blob([blob], { type: blob.type || 'application/octet-stream' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(fileBlob);
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        this.spinner.hide();
      },
      error: (err) => {
        // Optional: additional handling if you want to show custom UI error
        console.error('Error downloading file:', err);
        this.spinner.hide();
        this.onShowMessage(failureMessage);
      }
    });
  }

  onShowMessage(message?: string) {
    if (message && message.length > 0) {
      const modalRef = this.modalService.open(MessageComponent);
      modalRef.componentInstance.message = message;
    }
  }

  public SaveFile(data: any, fileName: string, fileType: FileType): void {
    this.spinner.show();
    switch (fileType) {
      case FileType.XLSX: {
        this.exportAsExcelFile(data, fileName);
      } break;
    }
    this.spinner.hide();
  }

  exportAsExcelFile(datas: ExcelData[], fileName: string): void {
    let workbook = XLSX.utils.book_new();
    datas.forEach(data => {
      let worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data.rows, { skipHeader: data.isHideAutoHeader });

      if (data.mergeColumns) {
        let merge = [];
        for (let i = 0; i < data.mergeColumns.length; i++) {
          let mergeColumn = data.mergeColumns[i];
          mergeColumn.mergeRow = mergeColumn.mergeRow - 1;
          merge.push({
            s: { r: mergeColumn.mergeRow, c: mergeColumn.startColumn },
            e: { r: mergeColumn.mergeRow, c: mergeColumn.endColumn }
          });
        }
        worksheet["!merges"] = merge;
      }

      if (data.topHiddenRows) {
        worksheet['!rows'] = [];
        for (let i = 0; i < data.topHiddenRows; i++) {
          worksheet['!rows'][i] = { hidden: true };
        }
      }

      if (data.numberOfColumns) {
        worksheet['!cols'] = [];
        for (let i = 0; i < data.numberOfColumns; i++) {
          worksheet['!cols'][i] = { width: 20 };
        }
      }

      if (data.topHiddenColumns) {
        if (!data.numberOfColumns)
          worksheet['!cols'] = [];
        for (let i = 0; i < data.topHiddenColumns; i++) {
          worksheet['!cols'][i] = { hidden: true };
        }
      }

      if (data.lookUps) {
        for (let i = 0; i < data.lookUps.length; i++) {
          let lookup = data.lookUps[i];
          let hiddenRows = data.topHiddenRows ? data.topHiddenRows : 0;
          let startIndex = (lookup.rowFrom ? lookup.rowFrom : 0) - hiddenRows;
          for (let j = startIndex - 1; j < data.rows.length; j++) {
            let column = lookup.columnName + (j + hiddenRows);
            worksheet[column].c = [];
            worksheet[column].c.hidden = true;
            worksheet[column].c.push({ a: "SheetJS", t: lookup.values.join(" ; ") });
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, data.sheetName);
    });
    let buffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
    let data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    fileSaver.saveAs(data, fileName + FileType.XLSX);
  }
}

export interface ExcelData {
  sheetName: string;
  rows: any;
  topHiddenRows?: number;
  topHiddenColumns?: number;
  numberOfColumns?: number;
  mergeColumns?: MergeExcelColumns[];
  isHideAutoHeader?: boolean;
  lookUps?: LookUp[];
  type?: number;
}

export enum ExcelDownloadTemplateType {
  None = 0,
  BulkSerialNumbers = 1,
  BulkUsers = 2,
  QrLabelGenerator = 3
}

export interface MergeExcelColumns {
  mergeRow: number;
  startColumn: number;
  endColumn: number;
}

export interface LookUp {
  rowFrom?: number;
  columnName: string;
  values: string[];
}

export enum FileType {
  XLSX = ".xlsx",
  TXT = ".txt",
  BAT = ".bat",
  ZIP = ".zip"
}